//External import
import { Theme, createTheme, alpha } from "@mui/material";
import cssBaselineOverrides from "./cssBaselineOverrides";
import type { } from "@mui/lab/themeAugmentation";
import "@mui/lab/themeAugmentation";
//Internal import
import { getLightPalette } from "../palettes";
import variantsGenerator from "./typographyVariantsGenerator";

/**
 * Place all theme related properties here (except for palette)
 * @param {boolean} dark is used to switch theme
 * Since designs currently have only one theme, don't provide dark argument
 */

const createMyTheme = (primaryColor: string, dark?: boolean): Theme => {
 
  const palette = getLightPalette(primaryColor);  
  const textColor = dark ? `rgba(255,255,255,0.87)` : `rgba(0,0,0,0.87)`;
  const tabsBgcolor = dark ? `${alpha(palette.primary.main, 0.22)}` : `${alpha(palette.primary.main, 0.1)}`

  const theme = createTheme({
    palette,
    direction: "rtl",
    typography: {
      fontFamily: "Vazir !important",
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            color: textColor,
          },
        },
      },
      MuiCssBaseline: cssBaselineOverrides(palette),
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: "5px",
            minHeight: 43,
            minWidth: 120,
          },
        },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              boxShadow: "none",
              color: "white",
            },
          },
        ],
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            [`& .MuiDrawer-paper`]: {
              borderRadius: 0,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
            padding: "16px",
            boxShadow: "none",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              borderRadius: "5px",
            },
          },
        },
        defaultProps: {
          autoComplete: "off",
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: "16px",
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: "1px solid",
            padding: "8px 16px",
            marginBottom: "16px",
            borderColor: alpha(palette.common.black, 0.12),
            boxShadow: `0px 2px 5px ${alpha(palette.common.black, 0.06)}`,
            "&.Mui-expanded": {
              marginBottom: "8px !important",
            },
            "& .MuiCollapse-root .MuiCollapse-wrapper": {
              "& .MuiAccordionDetails-root": {
                paddingTop: "20px",
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: tabsBgcolor,
            padding: "8px",
            borderRadius: "40px",
            borderBottom: "0px !important",
            "& .MuiTabs-scroller ": {
              "& .MuiTabs-flexContainer button": {
                "&.Mui-selected": {
                  background: palette.primary.main,
                  borderRadius: "40px",
                  color: palette.common.white,
                },
                fontSize: 16,
              },
              "& span": {
                display: "none",
              },
            },
          },
        },
      },
      MuiTimelineDot: {
        styleOverrides: {
          root: {
            display: "flex",
            alignSelf: "baseline",
            borderStyle: "solid",
            borderWidth: "2px",
            padding: "4px",
            borderRadius: "50%",
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
            margin: "11.5px 0px",
            borderColor: "transparent",
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            "& .MuiMenu-paper": {
              boxShadow: `0 1px 4px ${alpha(palette.common.black, 0.2)}`,
            },
          },
        },
      },
    },
  });

  const media = theme.breakpoints.down("md");

  /* Responsive typographies here (only base typographies(for example header. not headerBold)) or default MUI */
  theme.typography.large = {
    fontSize: "48px",
    lineHeight: "75px",
    [media]: {
      fontSize: "22px",
      lineHeight: "28px",
    },
  };
  theme.typography.subheading = {
    fontSize: "32px",
    lineHeight: "39px",
    [media]: {
      fontSize: "24px",
      lineHeight: "29px",
    },
  };
  theme.typography.heading = {
    fontSize: "24px",
    lineHeight: "39px",
    [media]: {
      fontSize: "16px",
      lineHeight: "22px",
    },
  };
  theme.typography.subtitle = {
    fontSize: "18px",
    lineHeight: "30px",
    [media]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  };
  theme.typography.body = {
    fontSize: "16px",
    lineHeight: "26px",
    [media]: {
      fontSize: "14px",
      lineHeight: "22px",
    },
  };
  theme.typography.small = {
    fontSize: "14px",
    lineHeight: "22px",
    [media]: {
      fontSize: "12px",
      lineHeight: "20px",
    },
  };
  theme.typography.extraSmall = {
    fontSize: "12px",
    lineHeight: "20px",
    [media]: {
      fontSize: "10px",
      lineHeight: "16px",
    },
  };
  theme.typography = {
    ...theme.typography,
    ...variantsGenerator(theme.typography),
  };

  return theme;
};

export default createMyTheme;
